import {
  Decoration,
  EditorView,
  Range,
  ReactCodeMirrorRef,
  StateEffect,
  StateField,
} from "@uiw/react-codemirror";
import * as sourceMap from "json-source-map";

import { Audit, auditVerdict } from "@/components/canvas-editor/shapes/audit";
import { FeedVersion } from "@/lib/types";

const success_effect = StateEffect.define<Range<Decoration>[]>();
export const removeSuccess = StateEffect.define<null>();
export const success_extension = StateField.define({
  create() {
    return Decoration.none;
  },
  update(value, transaction) {
    value = value.map(transaction.changes);

    for (const effect of transaction.effects) {
      if (effect.is(success_effect)) {
        value = value.update({
          add: effect.value,
          sort: true,
        });
      } else if (effect.is(removeSuccess)) {
        value = Decoration.none;
      }
    }

    return value;
  },
  provide: (f) => EditorView.decorations.from(f),
});

const error_effect = StateEffect.define<Range<Decoration>[]>();

export const removeError = StateEffect.define<null>();
export const error_extension = StateField.define({
  create() {
    return Decoration.none;
  },
  update(value, transaction) {
    value = value.map(transaction.changes);

    for (const effect of transaction.effects) {
      if (effect.is(error_effect)) {
        // Merge new decorations with existing ones instead of replacing
        value = value.update({
          add: effect.value,
          sort: true,
        });
      } else if (effect.is(removeError)) {
        value = Decoration.none;
      }
    }

    return value;
  },
  provide: (f) => EditorView.decorations.from(f),
});

export function annotateNodeJson(
  codeMirror: ReactCodeMirrorRef,
  jsonSourceMap: sourceMap.Pointers,
  feed: Omit<FeedVersion, "metadata" | "created_at">,
  // shape: CustomShapes,
  audit: Audit | undefined,
  path: string | undefined,
) {
  // "undecided" falls through both branches below and is left undecorated, deliberately:
  // painting it red would state that the leaf rejected the post when nothing evaluated it.
  const resultState = auditVerdict(audit);

  // `_explain_error` is an ERROR STRING the engine adds beside the operator, not an operator.
  // Key order is whatever the serialiser produced, so without excluding it this can pick it as
  // the operator name and build the pointer `/manifest/filter/_explain_error`, which matches
  // nothing in the source map — and the highlight silently does not happen.
  const key = Object.keys(audit?.condition || {}).filter(
    (k) => k !== "metadata" && !k.startsWith("_"),
  )[0];

  // if there is a path we just want a reference to the object, not just the feild
  const jsonPath = path ?? `/manifest/filter/${key}`;
  const jsonValue = jsonSourceMap[jsonPath];

  if (jsonValue) {
    if (resultState === "success") {
      codeMirror.view?.dispatch({
        effects: success_effect.of([
          Decoration.mark({
            attributes: {
              "data-result": `${audit?.scores[0] || ""}`,
            },
            class:
              "text-green-dim relative json-result before:content-[''] before:absolute before:inset-[-2px] before:bg-green-4 before:z-[-10]",
          }).range(jsonValue.value.pos, jsonValue.valueEnd.pos),
        ]),
      });
    } else if (resultState === "failure") {
      codeMirror.view?.dispatch({
        effects: error_effect.of([
          Decoration.mark({
            attributes: {
              "data-result": `${audit?.scores[0] || ""}`,
            },
            class:
              "text-red-dim relative json-result before:content-[''] before:absolute before:inset-[-2px] before:bg-red-4 before:z-[-10]",
          }).range(jsonValue.value.pos, jsonValue.valueEnd.pos),
        ]),
      });
    }
  }

  if (audit?.sub_audit?.length) {
    for (let i = 0; i < audit.sub_audit.length; i++) {
      const subAudit = audit.sub_audit[i];

      annotateNodeJson(
        codeMirror,
        jsonSourceMap,
        feed,
        subAudit,
        // when there is a path we need to insert the key
        path ? `${jsonPath}/${key}/${i}` : `${jsonPath}/${i}`,
      );
    }
  }
}
